import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {CellClicked} from '@components/ui/table-template/models/cell-clicked';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {saveAs} from 'file-saver';
import {finalize} from 'rxjs/operators';
import {DocumentViewModel} from './models/document.view-model';
import {DocumentCardComponent} from './document-card/document-card.component';
import {DocumentConverterService} from './services/document-converter.service';
import {DocumentsService} from './services/documents.service';
import {Title} from '@angular/platform-browser';
import {Keys} from '@shared/keys';

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClientsideDataService],
  imports: [LoadingAnimationComponent, TableTemplateComponent, DocumentCardComponent]
})
export class DocumentsComponent implements OnInit, TableTemplateComponentHost {

  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly documentService = inject(DocumentsService);
  private readonly clientSideDataService = inject<ClientsideDataService<DocumentViewModel>>(ClientsideDataService);
  private readonly documentConverter = inject(DocumentConverterService);
  private readonly titleService = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('uploadedDateTime');
  readonly sortOrder = signal(-1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<DocumentViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  private readonly SEARCH_FIELDS: Keys<DocumentViewModel> = ['clientName', 'documentTitle'];

  ngOnInit() {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.titleService.setTitle('Portal - Documents');
    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Documents);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    this.documentService.getDocumentsList()
      .pipe(
        finalize(() => {
          this.dataLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: documents => {
          const documentViewModels = this.documentConverter.convert(documents);
          this.clientSideDataService.initialize(documentViewModels);
          this.updateData();
          this.dataEmpty.set(documentViewModels.length === 0);
          this.dataAvailable.set(!this.dataEmpty());
        },
        error: error => {
          console.error('Error loading documents', error);
          this.dataEmpty.set(true);
          this.dataAvailable.set(false);
        }
      });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(_dataViewModel: DataViewModel): void {
    // Row clicking is not supported in this view
  }

  cellClicked(cellClicked: CellClicked): void {
    const documentVM = cellClicked.row as DocumentViewModel;
    this.downloadDocument(documentVM);
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Entity',
        dataProperty: 'clientName',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Title',
        dataProperty: 'documentTitle',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '20%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'File Format',
        dataProperty: 'fileExtension',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '15%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Date and Time',
        dataProperty: 'uploadedDateTime',
        cellType: CellType.DateTime,
        sortable: true,
        alignment: Alignment.Left,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: '',
        dataProperty: 'downloadButtonText',
        cellType: CellType.Button,
        sortable: false,
        alignment: Alignment.Center,
        width: '25%',
        dataFocusable: false,
        headingFocusable: false
      }
    ]);
  }

  downloadDocument(documentVM: DocumentViewModel) {
    this.documentService.getClientDocument(documentVM.clientCode, documentVM.entryNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(documentBlob => {
        this.saveFile(documentBlob, documentVM.fileDownloadName);
      });
  }

  saveFile(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Documents, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }

}
